import '../styles/v2/pages/onwatch.css';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import InfoTooltip from '../components/InfoTooltip';
import { ArrowLeft, Loader2, Search, Video, Radio, AlertTriangle, Zap, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronRight } from 'lucide-react';

const POLL_INTERVAL = 15000;

const STATUS_FILTERS = ['All', 'Connected', 'Reconnecting', 'Disconnected', 'Unknown'];
const STATUS_CLASS = { CONNECTED: 'healthy', RECONNECTING: 'warning', DISCONNECTED: 'critical', UNKNOWN: 'unknown' };

const STREAM_TYPE_LABEL = {
  0: 'Camera/Zone', 1: 'Forensic/Still', 2: 'Point', 3: 'Forensics', 6: 'AI Camera', 7: 'Stills',
};

// Same informational/critical/unknown token reuse as Point health - a
// deliberately decommissioned accessory is neither a healthy claim nor an
// unhealthy one.
const ACCESSORY_HEALTH_CLASS = { HEALTHY: 'healthy', UNHEALTHY: 'critical', DISABLED: 'informational', UNKNOWN: 'unknown' };
const ACCESSORY_HEALTH_LABEL = { HEALTHY: 'Connected', UNHEALTHY: 'Unhealthy', DISABLED: 'Disabled', UNKNOWN: 'Unknown' };

// Points connect by socket, not RTSP: their /cameras stream entry reads
// DISCONNECTED for every healthy Point, always (confirmed with Oosto
// support). Health here comes only from metadata.pointHealth, computed
// server-side from GET /points (onwatch.normalizer.cjs's computePointHealth).
// DISABLED reuses the informational (blue) token deliberately: it is
// neither a healthy claim nor an unhealthy one, a distinct third state for
// a device someone chose to turn off.
const POINT_HEALTH_CLASS = { HEALTHY: 'healthy', UNHEALTHY: 'critical', DISABLED: 'informational', UNKNOWN: 'unknown' };
const POINT_HEALTH_LABEL = { HEALTHY: 'Active', UNHEALTHY: 'Unhealthy', DISABLED: 'Disabled', UNKNOWN: 'Unknown' };

// A syncInterval-aware threshold is the right long-term answer (a Point
// syncing hourly is not stale at 90 minutes, one syncing every minute is
// stale well before 24h) and belongs in Phase 2e alongside the alert
// rules, once additionalSettings.syncInterval is read. This 24h line is
// deliberately conservative in the meantime: safe across any plausible
// per-Point interval, never flags a correctly configured Point.
const SYNC_STALE_MS = 24 * 60 * 60 * 1000;

// Task B: the accessory is the physical relay wired to the door. Returns
// null when nothing should render (no accessory attached - many Points
// legitimately have none, and an empty slot must not read as a fault).
// A disabled Point is not trying to open anything, so its accessory is
// never flagged regardless of connection state - muted, not prominent.
function computeAccessoryIndicator(meta) {
  if (!meta || !meta.accessoryName) return null;
  const { accessoryConnected, pointHealth } = meta;
  if (pointHealth === 'DISABLED') return { connected: accessoryConnected, muted: true, prominent: false };
  if (accessoryConnected === false) return { connected: false, muted: false, prominent: true };
  if (accessoryConnected === true) return { connected: true, muted: false, prominent: false };
  return { connected: null, muted: false, prominent: false };
}

// Task C: absent sync data (point.sync was null) renders nothing - absent
// is not the same claim as "never synced", and only one Point's full
// payload has ever been observed live. A more recent lastFailedSync than
// lastSuccessfulSync is an actively failing sync and flags regardless of
// age; otherwise age alone against SYNC_STALE_MS decides. A disabled Point
// is not recognizing anyone, so a stale or failing watchlist sync is not a
// live access-control risk for it the way it is for an active Point - same
// "not interesting, do not flag" rule as the accessory indicator above,
// discovered live: point-classroom's lastSuccessfulSync is months old and
// must not render as a warning.
function computeSyncIndicator(meta, nowMs) {
  if (!meta) return null;
  const { syncState, lastSuccessfulSync, lastFailedSync, pointHealth } = meta;
  if (!syncState && !lastSuccessfulSync && !lastFailedSync) return null;
  const successMs = lastSuccessfulSync ? new Date(lastSuccessfulSync).getTime() : null;
  const failedMs = lastFailedSync ? new Date(lastFailedSync).getTime() : null;
  const muted = pointHealth === 'DISABLED';
  const failing = !muted && failedMs != null && (successMs == null || failedMs > successMs);
  const stale = !muted && !failing && successMs != null && (nowMs - successMs) > SYNC_STALE_MS;
  return { syncState, lastSuccessfulSync, failing, stale, muted };
}

function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}


function CameraDot({ status, stale }) {
  const cls = stale ? 'unknown' : (STATUS_CLASS[status] || 'unknown');
  return <span className={`ow-dot ${cls}`} title={stale ? 'UNKNOWN (stale)' : status} />;
}

function PointDot({ pointHealth, stale }) {
  const health = stale ? 'UNKNOWN' : (pointHealth || 'UNKNOWN');
  const cls = POINT_HEALTH_CLASS[health] || 'unknown';
  return <span className={`ow-dot ${cls}`} title={stale ? 'UNKNOWN (stale)' : (POINT_HEALTH_LABEL[health] || health)} />;
}

// Prompt 2L, Task E: channel-level tooltip, explaining channel numbers as
// physical relay outputs and why gaps in the sequence are normal (spare
// capacity or channels wired outside OnWatch's knowledge - channel count
// is never presented as "N of totalCapacity" for exactly this reason).
const CHANNEL_TOOLTIP = 'Channel numbers map to physical relay outputs on the controller. Gaps (e.g. channels 1 and 3 but no 2) are normal: spare capacity, or a channel wired outside OnWatch\'s knowledge.';
const CONTROLLER_TOOLTIP = 'Door controllers. One physical controller has several relay channels, each wired to a different door, so a single controller failing can take out several doors at once.';

/**
 * One row per channel within an expanded controller: channel number, its
 * own name (the door it is), and the cameras or Points it serves.
 */
function ChannelRow({ channel, titleByDeviceId, appStale }) {
  const health = appStale ? 'UNKNOWN' : (channel.health || 'UNKNOWN');
  const cameraTitles = (channel.linkedDeviceIds || []).map((id) => titleByDeviceId.get(id) || id);
  return (
    <div className={`ow-accessory-channel-row${health === 'UNHEALTHY' ? ' unhealthy' : ''}`}>
      <span className={`ow-dot ${ACCESSORY_HEALTH_CLASS[health] || 'unknown'}`} />
      <span className="ow-accessory-channel-label">ch{channel.channel ?? '?'}</span>
      <span className="ow-workload-name">{channel.name || channel.accessoryId}</span>
      <span className={`ow-badge ${ACCESSORY_HEALTH_CLASS[health] || 'unknown'}`}>
        {ACCESSORY_HEALTH_LABEL[health] || health}
      </span>
      {cameraTitles.length > 0 && (
        <span className="ow-form-help">{cameraTitles.length} camera{cameraTitles.length === 1 ? '' : 's'}: {cameraTitles.join(', ')}</span>
      )}
    </div>
  );
}

/**
 * Task B/A4: one row per physical controller. Collapsed by default;
 * auto-expanded when unhealthy so every affected door and camera is
 * visible without a click (Task B's explicit instruction: "one failure,
 * several consequences, stated together"). The user can still toggle
 * either way - expandedOverride wins over the health-based default once
 * set.
 */
function ControllerRow({ controller, titleByDeviceId, appStale, expandedOverride, onToggle }) {
  const health = appStale ? 'UNKNOWN' : (controller.health || 'UNKNOWN');
  const expanded = expandedOverride !== undefined ? expandedOverride : health === 'UNHEALTHY';

  return (
    <div className={`ow-accessory-controller${health === 'UNHEALTHY' ? ' unhealthy' : ''}`}>
      <div
        className={`ow-workload-row ow-accessory-row${appStale ? ' ow-stale' : ''}${health === 'DISABLED' ? ' suppressed' : ''}`}
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className={`ow-dot ${ACCESSORY_HEALTH_CLASS[health] || 'unknown'}`} />
        <span className="ow-workload-name">{controller.address || 'Unknown address'}</span>
        <span className="ow-kind-badge">{controller.accessoryType || 'Accessory'}</span>
        <div className="ow-workload-meta">
          <span className={`ow-badge ${ACCESSORY_HEALTH_CLASS[health] || 'unknown'}`}>
            {ACCESSORY_HEALTH_LABEL[health] || health}
          </span>
          <span className="ow-form-help">
            {controller.channelCount} channel{controller.channelCount === 1 ? '' : 's'}
            <InfoTooltip text={CHANNEL_TOOLTIP} />
          </span>
          {/* Task B: labeled by what the count actually is (linked cameras),
              never "device", which reads as a physical device count next
              to an address in a section about physical controllers. */}
          <span className="ow-form-help">{controller.totalLinkedDevices} camera{controller.totalLinkedDevices === 1 ? '' : 's'}</span>
        </div>
      </div>
      {expanded && (
        <div className="ow-accessory-channels">
          {controller.channels.map((c) => (
            <ChannelRow key={c.id} channel={c} titleByDeviceId={titleByDeviceId} appStale={appStale} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Task D: the API's own totalResults (13, live) exceeds the accessories
 * array it actually returns (11) - confirmed not a pagination default.
 * The UI must never silently show fewer than the API reports, so this
 * says so explicitly rather than rendering 11 controllers as if that
 * were the complete picture.
 */
function AccessoriesSection({ controllers, accessoryCount, accessoriesApiTotal, rows, appStale }) {
  const [expandedOverrides, setExpandedOverrides] = useState({});
  if (!controllers || controllers.length === 0) return null;
  const titleByDeviceId = new Map(rows.map((r) => [r.sourceId, r.title || r.sourceId]));
  const missingCount = accessoriesApiTotal != null && accessoriesApiTotal > accessoryCount ? accessoriesApiTotal - accessoryCount : 0;

  return (
    <div className="ow-accordion-section" id="accessories">
      <div className="ow-accordion-header" style={{ cursor: 'default' }}>
        <Zap size={15} />
        <span className="ow-accordion-title">
          Door controllers
          <InfoTooltip text={CONTROLLER_TOOLTIP} />
        </span>
        <span className="ow-accordion-count">{controllers.length}</span>
      </div>
      {missingCount > 0 && (
        <div className="ow-coverage-message informational" style={{ marginTop: 0 }}>
          Showing {accessoryCount} of {accessoriesApiTotal} accessories the appliance reports. The other {missingCount} do not
          appear in the API's own accessory list; cause not yet determined on the appliance side.
        </div>
      )}
      <div className="ow-accordion-body">
        {controllers.map((controller) => {
          const key = controller.address || controller.channels[0]?.accessoryId;
          return (
            <ControllerRow
              key={key}
              controller={controller}
              titleByDeviceId={titleByDeviceId}
              appStale={appStale}
              expandedOverride={expandedOverrides[key]}
              onToggle={() => setExpandedOverrides((prev) => ({
                ...prev,
                [key]: !(prev[key] !== undefined ? prev[key] : controller.health === 'UNHEALTHY'),
              }))}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function OnWatchCameras() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [instance, setInstance] = useState(null);
  const [rows, setRows] = useState([]);
  const [controllers, setControllers] = useState([]);
  const [accessoryCount, setAccessoryCount] = useState(0);
  const [accessoriesApiTotal, setAccessoriesApiTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  // B6: sortable latency p95 / 24h disconnects columns. null sortKey means
  // the default title-alphabetical order the API already returns.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [instRes, camRes, accRes] = await Promise.all([
        fetchWithTenant(`/api/onwatch/instances/${id}`),
        fetchWithTenant(`/api/onwatch/instances/${id}/cameras`),
        fetchWithTenant(`/api/onwatch/instances/${id}/accessories`),
      ]);
      if (!instRes.ok) throw new Error(`HTTP ${instRes.status}`);
      if (!camRes.ok) throw new Error(`HTTP ${camRes.status}`);
      const instData = await instRes.json();
      const camData = await camRes.json();
      setInstance(instData.instance);
      setRows(camData.cameras || []);
      // Best-effort: accessories are a supplementary section, never worth
      // failing the whole page load over.
      if (accRes.ok) {
        const accData = await accRes.json();
        setControllers(accData.controllers || []);
        setAccessoryCount(accData.accessoryCount ?? 0);
        setAccessoriesApiTotal(accData.accessoriesApiTotal ?? null);
      }
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
    const timer = setInterval(() => fetchAll(true), POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchAll]);

  // Same per-tier staleness treatment as the health page: the app tier
  // (cameras and Points both come from here, not Kubernetes) can be stale
  // independently of everything else, and this page must never show a
  // live status color while that is true.
  const appConfigured = !!(instance?.appLastSuccessAt || instance?.appLastError);
  const appStale = appConfigured && (
    !instance.appLastSuccessAt ||
    (Date.now() - new Date(instance.appLastSuccessAt).getTime()) / 1000 > (instance.stalenessSec || 300)
  );

  // Points and cameras are different devices with different failure modes;
  // separated into their own sections rather than one flat list with a
  // type badge, so a Point's DISABLED (blue) is never scanned past as if
  // it were a camera's DISCONNECTED (red) at a glance.
  const cameras = useMemo(() => rows.filter((c) => c.streamType !== 2), [rows]);
  const points = useMemo(() => rows.filter((c) => c.streamType === 2), [rows]);

  const filteredCameras = useMemo(() => {
    let list = cameras;
    if (statusFilter !== 'All') {
      const wanted = statusFilter.toUpperCase();
      list = list.filter((c) => c.status === wanted);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) => (c.title || '').toLowerCase().includes(q));
    }
    if (sortKey) {
      list = [...list].sort((a, b) => {
        // A camera with no count yet sorts to the end regardless of
        // direction, never treated as 0, which would put an unmeasured
        // camera at the quiet end of the list.
        const av = sortKey === 'unackAlertCount' ? a.unackAlertCount : a.disconnects24h;
        const bv = sortKey === 'unackAlertCount' ? b.unackAlertCount : b.disconnects24h;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return sortDir === 'asc' ? av - bv : bv - av;
      });
    }
    return list;
  }, [cameras, statusFilter, search, sortKey, sortDir]);

  function toggleSort(key) {
    if (sortKey !== key) { setSortKey(key); setSortDir('desc'); return; }
    setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
  }

  const filteredPoints = useMemo(() => {
    if (!search.trim()) return points;
    const q = search.trim().toLowerCase();
    return points.filter((p) => (p.title || '').toLowerCase().includes(q));
  }, [points, search]);

  // Maps a camera/Point's sourceId to the health of whichever accessory
  // serves it, so the inline per-device indicator can share color with
  // the full accessory record (A4: "link it to the accessory record")
  // rather than only reflecting the per-Point accessoryConnected snapshot.
  const accessoryHealthByDeviceId = useMemo(() => {
    const m = new Map();
    for (const controller of controllers) {
      for (const c of controller.channels) {
        for (const deviceId of c.linkedDeviceIds || []) m.set(deviceId, c.health);
      }
    }
    return m;
  }, [controllers]);

  const reconnectingCount = cameras.filter((c) => c.status === 'RECONNECTING').length;
  const unhealthyPointsCount = points.filter((p) => p.metadata?.pointHealth === 'UNHEALTHY').length;

  if (loading && !instance) {
    return (
      <div className="page-content">
        <div className="md-loading"><Loader2 size={32} className="spin" /><p>Loading cameras...</p></div>
      </div>
    );
  }

  if (error && !instance) {
    return (
      <div className="page-content">
        <div className="ow-form-error">{error}</div>
        <Link to={`/monitoring/onwatch/${id}`}><ArrowLeft size={14} /> Back to cluster health</Link>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Link to={`/monitoring/onwatch/${id}`} style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ArrowLeft size={12} /> Cluster health
      </Link>

      <div className="page-header-with-actions">
        <div>
          <h1 className="page-title"><Video size={24} /> Cameras</h1>
          <div className="ow-workload-detail-meta">
            {cameras.length} camera{cameras.length === 1 ? '' : 's'}, {points.length} point{points.length === 1 ? '' : 's'}
            {appStale && ' · stale (app tier not current)'}
          </div>
        </div>
      </div>

      {appStale && (
        <div className="ow-wizard-stale-note" style={{ marginBottom: 14 }}>
          The app tier has not reported successfully within the staleness window. Statuses below are shown
          gray, not live, as of {timeAgo(instance.appLastSuccessAt)}.
        </div>
      )}

      <div className="ow-toolbar">
        <div className="ow-search-wrap">
          <Search size={14} />
          <input className="form-input" placeholder="Search by title..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="ow-chip-row">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              className={`ow-chip${statusFilter === s ? ' active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s}{s === 'Reconnecting' && reconnectingCount > 0 ? ` (${reconnectingCount})` : ''}
            </button>
          ))}
        </div>
        <div className="ow-chip-row">
          {/* B6: sorting by 24h disconnects surfaces the flaky cameras
              immediately, the first question anyone asks. */}
          <button className={`ow-chip${sortKey === 'disconnects24h' ? ' active' : ''}`} onClick={() => toggleSort('disconnects24h')}>
            Disconnects (24h) {sortKey === 'disconnects24h' ? (sortDir === 'desc' ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} />}
          </button>
          <button className={`ow-chip${sortKey === 'unackAlertCount' ? ' active' : ''}`} onClick={() => toggleSort('unackAlertCount')}>
            Unacked alerts {sortKey === 'unackAlertCount' ? (sortDir === 'desc' ? <ArrowDown size={12} /> : <ArrowUp size={12} />) : <ArrowUpDown size={12} />}
          </button>
        </div>
      </div>

      <div className="ow-workload-legend">
        <span className="ow-workload-legend-item">
          Unacked alerts<InfoTooltip text="People detected and not yet cleared by an operator - a review-status count, not a system fault count." />
        </span>
        <span className="ow-workload-legend-item">
          Disconnects<InfoTooltip text="Connection state changes recorded in the window. Frequent disconnects usually indicate a network or camera issue rather than a server problem." />
        </span>
      </div>

      {reconnectingCount > 0 && statusFilter !== 'Reconnecting' && !appStale && (
        <div className="ow-camera-reconnecting-badge" style={{ marginBottom: 14 }}>
          <AlertTriangle size={13} /> {reconnectingCount} camera{reconnectingCount === 1 ? '' : 's'} reconnecting
          <button className="ow-exceptions-jump" onClick={() => setStatusFilter('Reconnecting')} style={{ marginLeft: 8 }}>
            View
          </button>
        </div>
      )}

      <div className="ow-accordion-section">
        <div className="ow-accordion-header" style={{ cursor: 'default' }}>
          <Video size={15} />
          <span className="ow-accordion-title">Cameras</span>
          <span className="ow-accordion-count">{filteredCameras.length}</span>
        </div>
        <div className="ow-accordion-body">
          {filteredCameras.length === 0 && <div className="ow-form-help" style={{ padding: 16 }}>No cameras match.</div>}
          {filteredCameras.map((c) => (
            <div
              key={c.id}
              className={`ow-workload-row${appStale ? ' ow-stale' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/monitoring/onwatch/${id}/cameras/${encodeURIComponent(c.sourceId)}`)}
            >
              <CameraDot status={c.status} stale={appStale} />
              <span className="ow-workload-name">{c.title || c.sourceId}</span>
              <span className="ow-kind-badge">{STREAM_TYPE_LABEL[c.streamType] ?? 'Unknown type'}</span>
              <div className="ow-workload-meta">
                <span className="ow-form-help">{c.unackAlertCount != null ? `${c.unackAlertCount} unacked` : '— unacked'}</span>
                <span className="ow-form-help">{c.disconnects24h > 0 ? `${c.disconnects24h} disconnect${c.disconnects24h === 1 ? '' : 's'} (24h)` : '0 disconnects (24h)'}</span>
                {c.metadata?.accessoryName && (
                  <a
                    className={`ow-badge ${ACCESSORY_HEALTH_CLASS[accessoryHealthByDeviceId.get(c.sourceId)] || 'informational'}`}
                    href="#accessories"
                    onClick={(e) => e.stopPropagation()}
                    title="Jump to this accessory"
                  >
                    <Zap size={11} /> {c.metadata.accessoryName}
                  </a>
                )}
                {c.location && <span className="ow-workload-phases">{c.location}</span>}
                <span className="ow-form-help">
                  {c.lastStatusChange ? `changed ${timeAgo(c.lastStatusChange)}` : 'no change recorded'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="ow-accordion-section">
        <div className="ow-accordion-header" style={{ cursor: 'default' }}>
          <Radio size={15} />
          <span className="ow-accordion-title">Points</span>
          <span className="ow-accordion-count">{filteredPoints.length}</span>
          {unhealthyPointsCount > 0 && !appStale && (
            <span className="ow-badge critical" style={{ marginLeft: 'auto' }}>{unhealthyPointsCount} unhealthy</span>
          )}
        </div>
        <div className="ow-accordion-body">
          {filteredPoints.length === 0 && <div className="ow-form-help" style={{ padding: 16 }}>No Points match.</div>}
          {filteredPoints.map((p) => {
            const health = p.metadata?.pointHealth || 'UNKNOWN';
            const accessory = computeAccessoryIndicator(p.metadata);
            const sync = computeSyncIndicator(p.metadata, Date.now());
            const asOf = appStale ? ` as of ${timeAgo(instance.appLastSuccessAt)}` : '';

            let accessoryClass = 'informational';
            let accessoryLabel = p.metadata?.accessoryName;
            if (accessory) {
              if (appStale) {
                accessoryClass = 'unknown';
                accessoryLabel = `${p.metadata.accessoryName}${asOf}`;
              } else if (accessory.muted) {
                accessoryClass = 'informational';
                accessoryLabel = p.metadata.accessoryName;
              } else if (accessory.prominent) {
                accessoryClass = 'critical';
                accessoryLabel = `${p.metadata.accessoryName} disconnected`;
              } else if (accessory.connected === true) {
                accessoryClass = 'informational';
                accessoryLabel = `${p.metadata.accessoryName} connected`;
              } else {
                accessoryClass = 'unknown';
                accessoryLabel = p.metadata.accessoryName;
              }
            }

            let syncClass = 'informational';
            let syncLabel = '';
            if (sync) {
              if (appStale) {
                syncClass = 'unknown';
                syncLabel = `Synced${asOf}`;
              } else if (sync.failing) {
                syncClass = 'critical';
                syncLabel = 'Sync failing';
              } else if (sync.stale) {
                syncClass = 'warning';
                syncLabel = `Sync stale, ${timeAgo(sync.lastSuccessfulSync)}`;
              } else if (sync.lastSuccessfulSync) {
                syncClass = 'informational';
                syncLabel = `Synced ${timeAgo(sync.lastSuccessfulSync)}`;
              } else {
                syncClass = 'informational';
                syncLabel = sync.syncState;
              }
            }

            return (
              <div
                key={p.id}
                className={`ow-workload-row${appStale ? ' ow-stale' : ''}${health === 'DISABLED' ? ' suppressed' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/monitoring/onwatch/${id}/cameras/${encodeURIComponent(p.sourceId)}`)}
                // The /cameras stream status is real diagnostic detail but
                // is deliberately NOT a second status badge here (it reads
                // DISCONNECTED for every healthy Point) - available on
                // hover only, clearly labelled as not a health indicator.
                title={`Stream status (not a health indicator): ${p.status}`}
              >
                <PointDot pointHealth={health} stale={appStale} />
                <span className="ow-workload-name">{p.title || p.sourceId}</span>
                <span className="ow-kind-badge">Point</span>
                <div className="ow-workload-meta">
                  <span className={`ow-badge ${appStale ? 'unknown' : (POINT_HEALTH_CLASS[health] || 'unknown')}`}>
                    {appStale ? 'Unknown' : (POINT_HEALTH_LABEL[health] || health)}
                  </span>
                  {accessory && (
                    <a
                      className={`ow-badge ${accessoryClass}`}
                      href="#accessories"
                      onClick={(e) => e.stopPropagation()}
                      title={`${p.metadata.accessoryType || 'Accessory'} at ${p.metadata.accessoryAddress || 'unknown address'} - jump to accessory record`}
                    >
                      {accessory.prominent && !appStale && <AlertTriangle size={12} />}
                      {accessoryLabel}
                    </a>
                  )}
                  {sync && <span className={`ow-badge ${syncClass}`}>{syncLabel}</span>}
                  {p.location && <span className="ow-workload-phases">{p.location}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AccessoriesSection
        controllers={controllers}
        accessoryCount={accessoryCount}
        accessoriesApiTotal={accessoriesApiTotal}
        rows={rows}
        appStale={appStale}
      />
    </div>
  );
}
